import { shipmentReference } from "@/lib/shipment-reference";
import { useState } from "react";
import {
  AlertTriangle,
  Clock,
  TrendingDown,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { useAnalyticsAlerts } from "@/api/analytics";
import { Skeleton } from "@/components/ui/skeleton";

interface AlertSectionProps {
  title: string;
  count: number;
  icon: React.ReactNode;
  color: "red" | "orange" | "yellow";
  children: React.ReactNode;
}

function AlertSection({
  title,
  count,
  icon,
  color,
  children,
}: AlertSectionProps) {
  const [isOpen, setIsOpen] = useState(count > 0);
  const colorClasses = {
    red: "text-red-600 bg-red-50 border-red-200",
    orange: "text-orange-600 bg-orange-50 border-orange-200",
    yellow: "text-yellow-600 bg-yellow-50 border-yellow-200",
  };

  if (count === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className={`w-full justify-between p-4 h-auto border ${colorClasses[color]}`}
        >
          <div className="flex items-center gap-3">
            {icon}
            <span className="font-medium">{title}</span>
            <Badge variant="destructive">{count}</Badge>
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-x border-b rounded-b-md p-4 bg-white">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function AlertsPanel() {
  const { data, isLoading, error } = useAnalyticsAlerts();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Needs Attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Needs Attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load alerts</p>
        </CardContent>
      </Card>
    );
  }

  const alerts = data;
  const totalAlerts =
    (alerts?.failedTasks?.count ?? 0) +
    (alerts?.pendingOrders?.count ?? 0) +
    (alerts?.decliningCustomers?.count ?? 0);

  if (totalAlerts === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-green-500" />
            All Clear
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No issues requiring attention
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          Needs Attention
          <Badge variant="destructive">{totalAlerts}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Failed Tasks */}
        <AlertSection
          title="Failed Tasks Today"
          count={alerts?.failedTasks?.count ?? 0}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="red"
        >
          {alerts?.failedTasks?.breakdown &&
            alerts.failedTasks.breakdown.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {alerts.failedTasks.breakdown.map((b) => (
                  <Badge key={b.reason} variant="outline">
                    {b.reason}: {b.count}
                  </Badge>
                ))}
              </div>
            )}
          <div className="space-y-2">
            {alerts?.failedTasks?.items?.slice(0, 5).map((task) => (
              <div
                key={task.taskId}
                className="flex justify-between items-center text-sm py-2 border-b last:border-0"
              >
                <div>
                  <span className="font-medium">
                    {shipmentReference(task.shipmentNumber)}
                  </span>
                  <span className="text-muted-foreground">
                    {" "}
                    · TSK-{task.taskNumber}
                  </span>
                  {task.orderNumber && (
                    <span className="text-muted-foreground">
                      {" "}
                      (ORD-{task.orderNumber})
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    {" "}
                    - {task.customerName}
                  </span>
                </div>
                <div className="text-right">
                  <Badge variant="outline" className="text-red-600">
                    {task.reason}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </AlertSection>

        {/* Pending Orders */}
        <AlertSection
          title={`Orders Pending > ${alerts?.pendingOrders?.threshold || "4 hours"}`}
          count={alerts?.pendingOrders?.count ?? 0}
          icon={<Clock className="h-5 w-5" />}
          color="orange"
        >
          <div className="space-y-2">
            {alerts?.pendingOrders?.items?.slice(0, 5).map((order) => (
              <div
                key={order.orderId}
                className="flex justify-between items-center text-sm py-2 border-b last:border-0"
              >
                <div>
                  <span className="font-medium">ORD-{order.orderNumber}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    - {order.customerName}
                  </span>
                </div>
                <Badge variant="outline" className="text-orange-600">
                  {order.hoursAgo}h ago
                </Badge>
              </div>
            ))}
          </div>
        </AlertSection>

        {/* Declining Customers */}
        <AlertSection
          title={`Declining Customers (${alerts?.decliningCustomers?.threshold || "-30%"})`}
          count={alerts?.decliningCustomers?.count ?? 0}
          icon={<TrendingDown className="h-5 w-5" />}
          color="yellow"
        >
          <div className="space-y-2">
            {alerts?.decliningCustomers?.items?.slice(0, 5).map((customer) => (
              <div
                key={customer.customerId}
                className="flex justify-between items-center text-sm py-2 border-b last:border-0"
              >
                <div>
                  <span className="font-medium">{customer.name}</span>
                  {customer.customerNumber && (
                    <span className="text-muted-foreground">
                      {" "}
                      #{customer.customerNumber}
                    </span>
                  )}
                </div>
                <div className="text-right text-xs">
                  <span className="text-muted-foreground">
                    {customer.previousOrders} → {customer.currentOrders} orders
                  </span>
                  <Badge variant="outline" className="ml-2 text-yellow-600">
                    {customer.changePercent.toFixed(0)}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </AlertSection>
      </CardContent>
    </Card>
  );
}
